<template>
    <div class="tw-relative">
        <div class="tw-my-3 tw-flex tw-flex-col">
            <div class="tw-mb-3 tw-flex tw-items-center tw-gap-2">
                <label :for="name" class="tw-font-medium">{{ label }}</label>
                <InfoTooltip 
                    message="Warum fragen wir das? Damit wir Deine Traumreise perfekt für Dich planen können, ruft Dich einer unserer Reiseexperten gegebenenfalls kurz an - ganz unverbindlich."
                    aria-label="Information warum wir die Telefonnummer benötigen"
                />
            </div>
            <div 
                class="tw-flex tw-items-center tw-rounded-sm"
                :class="[
                    isActive ? 'tw-border-2 tw-border-black' : isHover ? 'tw-border tw-border-black' : 'tw-border tw-border-gray-300'
                ]"
                @mouseover="isHover = true"
                @mouseleave="isHover = false"
            >
                <!-- Custom dropdown -->
                <div class="tw-relative">
                    <div
                        class="tw-flex tw-items-center tw-p-2 tw-cursor-pointer"
                        @click="toggleDropdown()"
                        @touchstart.prevent="toggleDropdown()"
                        @focus="isActive = true"
                        @blur="handleBlur"
                        tabindex="0"
                        role="button"
                        aria-haspopup="listbox"
                        :aria-expanded="dropdownOpen"
                    >
                        <span>{{ getFlagEmoji(selectedCountry) }}</span>
                        <span class="tw-ml-2">+{{ dialCodes[selectedCountry] || '' }}</span>
                        <svg class="tw-ml-2" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </div>
                </div>
                <input
                    :id="name"
                    type="tel"
                    v-model="phone"
                    :placeholder="'660 12 34 567'"
                    @input="emitPhone"
                    @focus="isActive = true"
                    @blur="handleBlur"
                    class="tw-border-0 tw-p-2 tw-flex-1 tw-bg-transparent"
                />
            </div>
        </div>
        <!-- Dropdown menu rendered at root level -->
        <Teleport to="body">
            <div v-if="dropdownOpen" class="tw-fixed" :style="dropdownStyle">
                <div class="tw-bg-white tw-border tw-border-gray-300 tw-rounded-sm tw-shadow-lg tw-w-max tw-max-h-60 tw-overflow-auto">
                    <div v-for="country in priorityCountries" :key="country.code" 
                        @click="selectCountry(country.code)" 
                        class="tw-flex tw-items-center tw-p-2 tw-cursor-pointer hover:tw-bg-gray-100 touch-action-pan-y">
                        <span>{{ getFlagEmoji(country.code) }}</span>
                        <span class="tw-ml-2">+{{ dialCodes[country.code] || '' }}</span>
                        <span class="tw-ml-2 tw-text-gray-500">{{ country.name }}</span>
                    </div>
                    <div class="tw-border-t tw-my-1"></div>
                    <div v-for="country in remainingCountries" :key="country.code" 
                        @click="selectCountry(country.code)" 
                        class="tw-flex tw-items-center tw-p-2 tw-cursor-pointer hover:tw-bg-gray-100 touch-action-pan-y">
                        <span>{{ getFlagEmoji(country.code) }}</span>
                        <span class="tw-ml-2">+{{ dialCodes[country.code] || '' }}</span>
                        <span class="tw-ml-2 tw-text-gray-500">{{ country.name }}</span>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script>
import countries from 'i18n-iso-countries';
import de from 'i18n-iso-countries/langs/de.json';
import dialCodes from '../../data/dialCodes.js';
import InfoTooltip from './InfoTooltip.vue';

countries.registerLocale(de);

export default {
    components: {
        InfoTooltip,
    },
    props: {
        label: {
            type: String,
            default: 'Telefon',
        },
        value: {
            type: String,
            default: '',
        },
        country: {
            type: String,
            default: 'DE',
        },
        name: {
            type: String,
            default: 'phone',
        },
    },
    data() {
        const allCountries = countries.getNames('de');
        const priorityCountriesCodes = ['DE', 'AT', 'CH'];
        
        return {
            allCountries,
            priorityCountriesCodes,
            selectedCountry: this.country,
            phone: this.value,
            dialCodes,
            dropdownOpen: false,
            dropdownPosition: { top: 0, left: 0 },
            isActive: false,
            isHover: false,
        };
    },
    computed: {
        priorityCountries() {
            return this.priorityCountriesCodes
                .filter(code => this.allCountries[code] && this.dialCodes[code])
                .map(code => ({ code, name: this.allCountries[code] }));
        },
        remainingCountries() {
            return Object.entries(this.allCountries)
                .filter(([code]) => !this.priorityCountriesCodes.includes(code) && this.dialCodes[code])
                .map(([code, name]) => ({ code, name }))
                .sort((a, b) => a.name.localeCompare(b.name, 'de'));
        },
        dropdownStyle() {
            const trigger = this.$el.querySelector('.tw-cursor-pointer');
            if (!trigger) return {};
            
            const rect = trigger.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownHeight = 240; // Approximate max height of dropdown
            
            // Calculate if dropdown would go below viewport
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            // Position dropdown above trigger if not enough space below
            const top = spaceBelow < dropdownHeight && spaceAbove > spaceBelow
                ? `${rect.top - dropdownHeight}px`
                : `${rect.bottom}px`;
            
            return {
                top,
                left: `${rect.left}px`,
                maxHeight: `${Math.min(dropdownHeight, spaceBelow)}px`,
            };
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('touchstart', this.handleClickOutside);
        this.checkMissingDialCodes();
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        document.removeEventListener('touchstart', this.handleClickOutside);
    },
    watch: {
        value(val) {
            if (!val || val === this.phone) return;
            
            const countryCode = this.dialCodes[this.selectedCountry] || '';
            // Remove '00' prefix if present
            let processedVal = val.startsWith('00') ? val.slice(2) : val;
            
            // Remove country code if present
            if (processedVal.startsWith(countryCode)) {
                this.phone = processedVal.slice(countryCode.length);
            } else {
                this.phone = processedVal;
            }
        },
        country(val) {
            this.selectedCountry = val;
        },
    },
    methods: {
        checkMissingDialCodes() {
            const allCountries = [...this.priorityCountries, ...this.remainingCountries];
            const missingDialCodes = allCountries.filter(country => !this.dialCodes[country.code]);
            if (missingDialCodes.length > 0) {
                console.warn('Countries missing dial codes:', missingDialCodes);
            }
        },
        emitCountry() {
            this.$emit('countryChange', this.selectedCountry);
        },
        emitPhone() {
            const countryCode = this.dialCodes[this.selectedCountry] || '';
            const phoneWithoutSpaces = this.phone ? this.phone.replace(/\s+/g, '') : '';
            const fullPhone = phoneWithoutSpaces ? '00' + countryCode + phoneWithoutSpaces.replace(/^0+/, '') : '';
            this.$emit('change', fullPhone);
        },
        getFlagEmoji(countryCode) {
            // Converts ISO country code to regional indicator symbols
            // e.g. 'AT' -> 🇦🇹
            return countryCode
                .toUpperCase()
                .replace(/./g, char =>
                    String.fromCodePoint(127397 + char.charCodeAt())
                );
        },
        selectCountry(code) {
            this.selectedCountry = code;
            this.emitCountry();
            this.dropdownOpen = false;
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.dropdownOpen = false;
            }
        },
        toggleDropdown() {
            this.dropdownOpen = !this.dropdownOpen;
        },
        handleBlur(event) {
            // Only set isActive to false if neither the input nor dropdown are focused
            if (!this.$el.contains(document.activeElement)) {
                this.isActive = false;
                // Emit focusout event to parent component for validation
                this.$emit('focusout', event);
            }
        },
    },
};
</script>

<style scoped>
/* Remove select styles, as we now use a custom dropdown */
input:focus {
    outline: none;
}

/* Ensure the container maintains its border styles when focused */
.tw-border-2 {
    border-width: 2px;
}

/* Disable Safari's default active state styles */
input:focus,
input:focus-visible,
[role="button"]:focus,
[role="button"]:focus-visible {
    -webkit-tap-highlight-color: transparent;
    -webkit-appearance: none;
    outline: none;
}
</style> 